import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';

const PublicFAQ = () => {
  const { slug } = useParams();
  const [faq, setFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [allExpanded, setAllExpanded] = useState(false);

  useEffect(() => {
    fetchFaq();
  }, [slug]);

  const fetchFaq = async () => {
    try {
      const response = await axios.get(`/api/faq/public/${slug}`);
      console.log('FAQ данные:', response.data);
      console.log('🎨 Тема FAQ:', response.data.theme);
      console.log('Категории:', response.data.categories);
      response.data.categories.forEach((category, index) => {
        console.log(`Категория ${index}:`, {
          name: category.name,
          title: category.title,
          label: category.label,
          categoryName: category.categoryName,
          fullObject: category
        });
      });
      setFaq(response.data);
    } catch (error) {
      console.error('Ошибка получения FAQ:', error);
      setError('FAQ не найден или недоступен');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedQuestions(newExpanded);
  };

  // Функция для извлечения текста из HTML
  const getTextFromHTML = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Функция для обрезки текста до 100 символов
  const truncateText = (html, maxLength = 100) => {
    const text = getTextFromHTML(html);
    if (text.length <= maxLength) {
      return { truncated: html, needsExpand: false };
    }
    // Обрезаем текст и находим соответствующий HTML
    const truncatedText = text.substring(0, maxLength) + '...';
    return { truncated: truncatedText, needsExpand: true };
  };

  const toggleCategory = (categoryIndex) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryIndex)) {
      newExpanded.delete(categoryIndex);
    } else {
      newExpanded.add(categoryIndex);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleAllCategories = () => {
    if (allExpanded) {
      setExpandedCategories(new Set());
      setAllExpanded(false);
    } else {
      const newExpanded = new Set();
      filteredCategories.forEach((_, index) => {
        newExpanded.add(index);
      });
      setExpandedCategories(newExpanded);
      setAllExpanded(true);
    }
  };

  const filteredCategories = faq?.categories.map(category => ({
    ...category,
    questions: category.questions.filter(question =>
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0) || [];

  // Автоматически раскрываем категории при поиске
  useEffect(() => {
    if (searchTerm && filteredCategories.length > 0) {
      const newExpanded = new Set();
      filteredCategories.forEach((_, index) => {
        newExpanded.add(index);
      });
      setExpandedCategories(newExpanded);
    }
  }, [searchTerm, filteredCategories.length]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error || !faq) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">FAQ не найден</h2>
        <p className="text-gray-600 mb-6">
          Возможно, FAQ был удален или сделан приватным
        </p>
        <a
          href="/"
          className="text-orange-600 hover:text-orange-700"
        >
          Вернуться на главную
        </a>
      </div>
    );
  }

  console.log('🎨 Рендерим FAQ с темой:', faq.theme);
  console.log('🎨 Цвет фона категорий:', faq.theme?.categoryBgColor);
  console.log('🎨 Цвет наведения категорий:', faq.theme?.categoryHoverColor);
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 
          className="text-4xl font-bold mb-4 faq-title"
          style={{ color: faq.theme?.generalColor || '#ea580c' }}
        >
          {faq.title}
        </h1>
        {faq.description && (
          <p className="text-xl text-gray-600 mb-6">{faq.description}</p>
        )}
      </div>

      {/* Search and Controls */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          {/* Search */}
          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search 
                className="h-5 w-5" 
                style={{ color: faq.theme?.generalColor || '#ea580c' }}
              />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: faq.theme?.generalColor || '#ea580c',
                borderWidth: '1px',
                borderStyle: 'solid',
                '--focus-ring-color': faq.theme?.generalColor || '#ea580c'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = faq.theme?.generalColor || '#ea580c';
                e.target.style.boxShadow = `0 0 0 2px ${faq.theme?.generalColor || '#ea580c'}33`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = faq.theme?.generalColor || '#ea580c';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="Поиск по вопросам и ответам..."
            />
          </div>
          
          {/* Toggle All Button */}
          {filteredCategories.length > 0 && (
            <button
              onClick={toggleAllCategories}
              className="px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2"
              style={{ 
                backgroundColor: faq.theme?.generalColor || '#ea580c',
                '--hover-color': faq.theme?.generalColor || '#ea580c'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = faq.theme?.generalColor ? 
                  `${faq.theme.generalColor}dd` : '#ea580cdd';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = faq.theme?.generalColor || '#ea580c';
              }}
            >
              {allExpanded ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Свернуть все
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4" />
                  Развернуть все
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Categories */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {searchTerm ? 'По вашему запросу ничего не найдено' : 'В этом FAQ пока нет вопросов'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {filteredCategories.map((category, categoryIndex) => {
            const isCategoryExpanded = expandedCategories.has(categoryIndex);
            return (
              <div 
                key={categoryIndex} 
                className="bg-white rounded-lg shadow-md overflow-hidden"
                style={{ 
                  borderColor: faq.theme?.borderColor || '#f97316',
                  borderWidth: faq.theme?.borderWidth || '1px',
                  borderStyle: 'solid'
                }}
              >
                {/* Заголовок категории с кнопкой сворачивания */}
                <button
                  className="w-full px-6 py-4 faq-category-header flex justify-between items-center transition-colors"
                  style={{ 
                    backgroundColor: faq.theme?.categoryBgColor || faq.theme?.secondaryColor || '#fef3c7'
                  }}
                  data-debug-bg={faq.theme?.categoryBgColor}
                  data-debug-fallback={faq.theme?.secondaryColor}
                  data-theme-debug={`bg:${faq.theme?.categoryBgColor}, border:${faq.theme?.borderColor}`}
                  onMouseEnter={(e) => {
                    e.target.style.setProperty('background-color', faq.theme?.categoryHoverColor || '#fed7aa', 'important');
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.setProperty('background-color', faq.theme?.categoryBgColor || faq.theme?.secondaryColor || '#fef3c7', 'important');
                  }}
                  onClick={() => toggleCategory(categoryIndex)}
                >
                  <div className="text-left pointer-events-none">
                    <h2 
                      className="text-lg font-medium faq-category-title"
                      style={{ color: faq.theme?.primaryColor || '#f97316' }}
                      dangerouslySetInnerHTML={{ __html: category._doc?.name || category.name || category.title || category.label || category.categoryName || `Категория ${categoryIndex + 1}` }}
                      data-debug-category-name={category._doc?.name || category.name || category.title || category.label || category.categoryName || `Категория ${categoryIndex + 1}`}
                    />
                    {(category._doc?.description || category.description) && (
                      <p 
                        className="text-gray-600 mt-2 text-sm"
                        dangerouslySetInnerHTML={{ __html: category._doc?.description || category.description }}
                      />
                    )}
                  </div>
                  <div className="flex items-center pointer-events-none">
                    {isCategoryExpanded ? (
                      <ChevronDown 
                        className="h-5 w-5 flex-shrink-0" 
                        style={{ color: faq.theme?.arrowColor || '#f97316' }}
                      />
                    ) : (
                      <ChevronRight 
                        className="h-5 w-5 flex-shrink-0" 
                        style={{ color: faq.theme?.arrowColor || '#f97316' }}
                      />
                    )}
                  </div>
                </button>

                {/* Содержимое категории (вопросы и ответы) */}
                {isCategoryExpanded && (
                  <div className="px-4 py-2">
                    <div className="divide-y divide-orange-200">
                      {category.questions.map((question, questionIndex) => {
                        const questionKey = `${categoryIndex}-${questionIndex}`;
                        const isQuestionExpanded = expandedQuestions.has(questionKey);
                        const answer = question._doc?.answer || question.answer;
                        const { truncated, needsExpand } = truncateText(answer, 100);
                        const shouldHideAnswer = faq.hideAnswers && needsExpand;

                        return (
                          <div key={questionIndex} className="faq-question">
                          {/* Вопрос - всегда видимый */}
                          <div 
                            className="px-6 py-4"
                            style={{ 
                              backgroundColor: faq.theme?.questionBgColor || '#f9fafb',
                              borderColor: faq.theme?.contentBorderColor || '#e5e7eb',
                              borderWidth: faq.theme?.contentBorderWidth || '1px',
                              borderStyle: faq.theme?.contentBorderWidth === '0px' ? 'none' : 'solid'
                            }}
                          >
                            <h3 
                              className="font-medium text-gray-900 text-base"
                              dangerouslySetInnerHTML={{ __html: question._doc?.question || question.question }}
                            />
                          </div>
                          
                          {/* Ответ - раскрывается если hideAnswers=true */}
                          <div 
                            className="faq-question-content px-6 py-4"
                            style={{ 
                              backgroundColor: faq.theme?.answerBgColor || '#ffffff',
                              borderColor: faq.theme?.contentBorderColor || '#e5e7eb',
                              borderWidth: faq.theme?.contentBorderWidth || '1px',
                              borderStyle: faq.theme?.contentBorderWidth === '0px' ? 'none' : 'solid'
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div 
                                className="prose max-w-none flex-1"
                                style={{ 
                                  color: '#374151',
                                  fontFamily: faq.theme?.fontFamily || 'inherit'
                                }}
                              >
                                {shouldHideAnswer && !isQuestionExpanded ? (
                                  <div dangerouslySetInnerHTML={{ __html: truncated }} />
                                ) : (
                                  <div dangerouslySetInnerHTML={{ __html: answer }} />
                                )}
                              </div>
                              {shouldHideAnswer && (
                                <button
                                  onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                                  className="ml-4 flex-shrink-0"
                                  title={isQuestionExpanded ? 'Свернуть' : 'Развернуть'}
                                >
                                  {isQuestionExpanded ? (
                                    <ChevronDown 
                                      className="h-5 w-5" 
                                      style={{ color: faq.theme?.arrowColor || '#f97316' }}
                                    />
                                  ) : (
                                    <ChevronRight 
                                      className="h-5 w-5" 
                                      style={{ color: faq.theme?.arrowColor || '#f97316' }}
                                    />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="text-center mt-12 py-8 border-t border-orange-200">
        <p className="text-gray-500 text-sm">
          Создано с помощью{' '}
          <a 
            href="/" 
            className="text-orange-600 hover:text-orange-700"
            style={{ color: faq.theme?.generalColor || '#ea580c' }}
          >
            UniFAQ
          </a>
        </p>
      </div>
    </div>
  );
};

export default PublicFAQ;

